import { Component } from "react";
import PropTypes from "prop-types";
import { v4 } from "uuid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import moment from "moment";

import { translate as t } from "../../../../infrastructure/translate/localization";
import { shouldDisableFlex } from "../../../../infrastructure/country-rules";
import { editBenefitModel } from "../../../../usecases/benefit-model/edit-benefit-model";
import { hideModal } from "../../../../usecases/commons/hide-modal";

import CircleButton from "../../commons/CircleButton";
import CheckBox from "../../commons/checkBox/CheckBox";
import mealsImg from "../../../../assets/img/meals.png";
import "./mealsBenefitsModal.scss";
import { MonetaryAmount } from "../../../domain/monetary-amount";

class MealsModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mealBenefitsConfig: null,
      disabledFlexMeal: null,
      showConfig: false,
      shouldLimitDailyAmount: false,
    };
  }

  componentDidMount() {
    window.addEventListener("keydown", this.handleKeyDown);
    const { benefit, country } = this.props;
    this.setState({
      mealBenefitsConfig: benefit.mealBenefitsConfig,
      disabledFlexMeal: shouldDisableFlex(country)
        ? true
        : benefit.disabledFlex["meal-benefit"] || false,
      maxDailyAmount: benefit?.mealBenefitsConfig?.maxDailyAmount
        ? MonetaryAmount.ofCurrency(benefit.mealBenefitsConfig.maxDailyAmount)
        : MonetaryAmount.ZERO,
      shouldLimitDailyAmount: !!benefit?.mealBenefitsConfig?.maxDailyAmount,
    });
  }

  componentWillUnmount() {
    window.removeEventListener("keydown", this.handleKeyDown);
    hideModal();
  }

  handleAccept = () => {
    const { benefit } = this.props;
    const { mealBenefitsConfig, disabledFlexMeal, maxDailyAmount } = this.state;
    const parsedMaxDailyAmount = maxDailyAmount?.equal(MonetaryAmount.ZERO)
      ? null
      : maxDailyAmount;

    editBenefitModel({
      ...benefit,
      mealBenefitsConfig: {
        ...mealBenefitsConfig,
        maxDailyAmount: parsedMaxDailyAmount,
      },
      disabledFlex: {
        ...benefit.disabledFlex,
        "meal-benefit": disabledFlexMeal,
      },
    });
    hideModal();
  };

  toggleDisabledFlexMeal = () => {
    this.setState((prevState) => ({
      disabledFlexMeal: !prevState.disabledFlexMeal,
    }));
  };

  handleChange = (benefit) => {
    // eslint-disable-next-line react/no-unused-state
    this.setState({ benefit });
  };

  handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      hideModal();
    }
  };

  printDayOfWeek = (index) => {
    const days = [
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
      "sunday",
    ];
    return t(`common-messages.${days[index]}`);
  };

  render() {
    const { benefit, country } = this.props;
    const { mealBenefitsConfig } = benefit;
    const {
      showConfig,
      disabledFlexMeal,
      maxDailyAmount,
      shouldLimitDailyAmount,
    } = this.state;

    const shouldDisableSaveButton =
      maxDailyAmount === null || maxDailyAmount === undefined
        ? shouldLimitDailyAmount
        : shouldLimitDailyAmount && maxDailyAmount.equal(MonetaryAmount.ZERO);

    return (
      <div className="modal-container">
        <CircleButton
          className="primary close-modal"
          icon="times"
          handlerClick={() => hideModal()}
        />
        <div className="meals-benefits">
          <div className="modal-title">
            <p className="title">{t("benefits-section.meal-benefit.meal")}</p>
          </div>
          <div className="modal-section header-image">
            <p className="section-text">
              {t("benefits-section.meal-benefit.meal-schedule")}
            </p>
            <img src={mealsImg} alt="meals" />
          </div>

          <table className="meals-benefits-advances">
            <tbody>
              {mealBenefitsConfig.dayOfWeek.map((day) => {
                const index = mealBenefitsConfig.dayOfWeek.indexOf(day);
                const checkDay = mealBenefitsConfig.dayOfWeek[index];

                return (
                  <tr key={v4()}>
                    <td>
                      <div
                        className="checkbox-select"
                        onClick={() => {
                          this.handleChange({
                            ...benefit,
                            mealBenefitsConfig: {
                              ...mealBenefitsConfig,
                              dayOfWeek: [
                                ...mealBenefitsConfig.dayOfWeek,
                                (mealBenefitsConfig.dayOfWeek[index] = {
                                  ...checkDay,
                                  enabled: !checkDay.enabled,
                                }),
                              ],
                            },
                          });
                        }}
                        role="presentation"
                      >
                        {day.enabled && <FontAwesomeIcon icon="check" />}
                      </div>
                    </td>

                    <td>
                      <p>{this.printDayOfWeek(index)}</p>
                    </td>

                    <td>
                      <input
                        name="from"
                        type="time"
                        placeholder="00:00"
                        maxLength={5}
                        value={day.from}
                        disabled={!day.enabled}
                        required={day.enabled}
                        pattern="([0-2][0-9]:[0-9]{2})"
                        onChange={(e) => {
                          this.handleChange({
                            ...benefit,
                            mealBenefitsConfig: {
                              ...mealBenefitsConfig,
                              dayOfWeek: [
                                ...mealBenefitsConfig.dayOfWeek,
                                (mealBenefitsConfig.dayOfWeek[index] = {
                                  ...checkDay,
                                  from: e.target.value,
                                }),
                              ],
                            },
                          });
                        }}
                      />
                    </td>
                    <td>
                      <input
                        name="to"
                        type="time"
                        placeholder="23:59"
                        maxLength={5}
                        value={
                          moment(day.to, "HH:mm").isAfter(
                            moment(day.from, "HH:mm"),
                            "hour"
                          )
                            ? day.to
                            : moment(day.from, "HH:mm")
                                .add(1, "hours")
                                .format("HH:mm")
                        }
                        disabled={!day.enabled}
                        required={day.enabled}
                        onChange={(e) => {
                          this.handleChange({
                            ...benefit,
                            mealBenefitsConfig: {
                              ...mealBenefitsConfig,
                              dayOfWeek: [
                                ...mealBenefitsConfig.dayOfWeek,
                                (mealBenefitsConfig.dayOfWeek[index] = {
                                  ...checkDay,
                                  to: e.target.value,
                                }),
                              ],
                            },
                          });
                        }}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {showConfig && (
            <div className="meals-benefits-config">
              <CheckBox
                wrapperStyles="wrapper-checkbox"
                active={shouldLimitDailyAmount}
                textDescription={t(
                  "benefits-section.meal-benefit.limit-daily-use-in-meals"
                )}
                handlerClick={() =>
                  this.setState((prevState) => {
                    const { shouldLimitDailyAmount: auxState } = prevState;
                    return {
                      shouldLimitDailyAmount: !auxState,
                      maxDailyAmount: MonetaryAmount.ZERO,
                    };
                  })
                }
              />
              {shouldLimitDailyAmount && (
                <p>
                  {t(
                    "benefits-section.meal-benefit.limit-daily-use-in-meals-description"
                  )}
                </p>
              )}

              <div className="inputs-group left-content">
                <div className="input">
                  <p className="quantity">
                    {t("benefits-section.meal-benefit.quantity")}
                  </p>

                  <input
                    name="quantity"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="00,00"
                    disabled={!shouldLimitDailyAmount}
                    value={maxDailyAmount?.toUnitary()}
                    onChange={(e) => {
                      const inputAmount = e.target.value;

                      if (inputAmount === "") {
                        this.setState({
                          maxDailyAmount: MonetaryAmount.ZERO,
                        });
                      } else {
                        const amount = MonetaryAmount.fromUnitary(
                          inputAmount,
                          maxDailyAmount?.currency
                        );

                        this.setState({
                          maxDailyAmount: MonetaryAmount.ofCurrency(amount),
                        });
                      }
                    }}
                  />
                  <span>€</span>
                </div>
              </div>

              <div className="inputs-group left-content">
                <CheckBox
                  wrapperStyles="wrapper-checkbox"
                  active={disabledFlexMeal}
                  disabled={shouldDisableFlex(country)}
                  className={
                    shouldDisableFlex(country)
                      ? "check-box-container disabled"
                      : null
                  }
                  textDescription={t(
                    "benefits-section.meal-benefit.meal-disabled-flex"
                  )}
                  handlerClick={this.toggleDisabledFlexMeal}
                />
                <p>
                  {t(
                    "benefits-section.meal-benefit.meal-disabled-flex-description"
                  )}
                </p>
              </div>
            </div>
          )}
          <div className="center-content">
            {!showConfig && (
              <button
                type="button"
                className="btn-line gray btn-icon"
                onClick={() => this.setState({ showConfig: true })}
              >
                <FontAwesomeIcon icon="plus" />
                {t("actions.config")}
              </button>
            )}
            <button
              type="button"
              className="btn btn-line ml-3"
              disabled={shouldDisableSaveButton}
              onClick={() => this.handleAccept(benefit)}
            >
              {t("actions.save")}
            </button>
          </div>
        </div>
      </div>
    );
  }
}

MealsModal.defaultProps = { country: "ES" };
MealsModal.propTypes = {
  mealBenefitsConfig: PropTypes.object.isRequired,
  benefit: PropTypes.object.isRequired,
  country: PropTypes.string,
};

export default MealsModal;
